<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { ArrowPathIcon, CalendarDaysIcon, ClockIcon } from '@heroicons/vue/24/outline'
import { useI18n } from '@/composable/useI18n'
import TimeRangeCustomModal from '@/components/Globals/TimeRangeFilter/TimeRangeCustomModal.vue'
import type { DashboardPeriodPreset, TimeRangeValue } from '@/components/Globals/TimeRangeFilter/types'
import {
  buildHoursRange,
  formatRangeSummaryShort,
  nowRounded,
} from '@/utils/date/date-time'

const PRESET_HOURS: Record<Exclude<DashboardPeriodPreset, 'custom'>, number> = {
  '1h': 1,
  '6h': 6,
  '24h': 24,
}

const props = withDefaults(
  defineProps<{
    isRefreshing: boolean
    isPollingEnabled?: boolean
    pollingIntervalSeconds?: number
    pollingIntervalOptions?: number[]
    defaultPreset?: Exclude<DashboardPeriodPreset, 'custom'>
    initialRange?: TimeRangeValue | null
  }>(),
  {
    isPollingEnabled: false,
    pollingIntervalSeconds: 30,
    pollingIntervalOptions: () => [5,10,15, 30, 60, 300],
    defaultPreset: '24h',
    initialRange: null,
  },
)

const emit = defineEmits<{
  refresh: []
  change: [value: TimeRangeValue]
  'update:isPollingEnabled': [value: boolean]
  'update:pollingIntervalSeconds': [value: number]
}>()

const { t } = useI18n()

const current = ref<TimeRangeValue | null>(null)
const showCustomModal = ref(false)

const presetOptions = computed(() =>
  (['1h', '6h', '24h'] as const).map((value) => ({
    value,
    label: t(`custom_dashboards.period_filter.presets.${value}`),
  })),
)

const customButtonLabel = computed(() => {
  if (current.value?.preset === 'custom') {
    return formatRangeSummaryShort(current.value.start, current.value.end)
  }
  return t('custom_dashboards.period_filter.custom')
})

const customModalInitialStart = computed(() => {
  if (current.value?.preset === 'custom') return current.value.start
  const end = nowRounded()
  return new Date(end.getTime() - 24 * 60 * 60 * 1000)
})

const customModalInitialEnd = computed(() => {
  if (current.value?.preset === 'custom') return current.value.end
  return nowRounded()
})

function emitChange(value: TimeRangeValue) {
  current.value = value
  emit('change', value)
}

function applyPreset(preset: Exclude<DashboardPeriodPreset, 'custom'>) {
  const { start, end } = buildHoursRange(PRESET_HOURS[preset])
  emitChange({ start, end, preset })
}

function openCustomModal() {
  showCustomModal.value = true
}

function closeCustomModal() {
  showCustomModal.value = false
}

function applyCustomRange(start: Date, end: Date) {
  emitChange({ start, end, preset: 'custom' })
  showCustomModal.value = false
}

function updatePollingInterval(event: Event) {
  const seconds = Number((event.target as HTMLSelectElement).value)
  if (Number.isFinite(seconds)) emit('update:pollingIntervalSeconds', seconds)
}

function formatPollingInterval(seconds: number) {
  if (seconds >= 60 && seconds % 60 === 0) {
    return t('custom_dashboards.polling.minutes', { count: seconds / 60 })
  }

  return t('custom_dashboards.polling.seconds', { count: seconds })
}

onMounted(() => {
  const initial = props.initialRange
  if (initial?.start && initial?.end) {
    current.value = {
      start: new Date(initial.start),
      end: new Date(initial.end),
      preset: initial.preset ?? props.defaultPreset,
    }
    return
  }

  applyPreset(props.defaultPreset)
})
</script>

<template>
  <div class="flex flex-wrap items-center justify-end gap-3">
    <button
      type="button"
      class="inline-flex items-center justify-center border border-stroke bg-white p-2 shadow-sm transition-colors hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-60"
      :disabled="isRefreshing"
      :aria-label="t('custom_dashboards.polling.refresh')"
      :title="t('custom_dashboards.polling.refresh')"
      @click="emit('refresh')"
    >
      <ArrowPathIcon
        class="h-4 w-4 shrink-0 text-body"
        :class="{ 'animate-spin': isRefreshing }"
      />
    </button>

    <div class="inline-flex items-center gap-2 border border-stroke bg-white p-1 shadow-sm">
      <span class="px-2 text-xs font-bold uppercase tracking-wider text-black">
        {{ t('custom_dashboards.polling.label') }}
      </span>

      <button
        type="button"
        role="switch"
        class="relative inline-flex h-6 w-11 items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
        :class="isPollingEnabled ? 'border-primary bg-primary' : 'border-stroke bg-gray-2'"
        :aria-checked="isPollingEnabled"
        :aria-label="t('custom_dashboards.polling.toggle')"
        :title="
          isPollingEnabled
            ? t('custom_dashboards.polling.disable')
            : t('custom_dashboards.polling.enable')
        "
        @click="emit('update:isPollingEnabled', !isPollingEnabled)"
      >
        <span
          class="inline-block h-4 w-4 bg-white shadow transition-transform"
          :class="isPollingEnabled ? 'translate-x-6' : 'translate-x-1'"
        />
      </button>

      <select
        :value="pollingIntervalSeconds"
        class="border border-stroke bg-white px-2 py-1 text-xs font-bold text-body disabled:cursor-not-allowed disabled:bg-gray-2 disabled:opacity-60"
        :disabled="!isPollingEnabled"
        :aria-label="t('custom_dashboards.polling.interval')"
        :title="t('custom_dashboards.polling.interval')"
        @change="updatePollingInterval"
      >
        <option
          v-for="seconds in pollingIntervalOptions"
          :key="seconds"
          :value="seconds"
        >
          {{ formatPollingInterval(seconds) }}
        </option>
      </select>
    </div>

    <div class="inline-flex items-center gap-1 border border-stroke bg-white p-1 shadow-sm">
      <div class="mr-1 flex items-center gap-1 border-r border-stroke px-3">
        <ClockIcon class="h-4 w-4 text-body" />
        <span class="text-xs font-bold uppercase tracking-wider text-black">
          {{ t('custom_dashboards.period_filter.label') }}
        </span>
      </div>

      <button
        v-for="preset in presetOptions"
        :key="preset.value"
        type="button"
        :class="[
          'px-3 py-1.5 text-xs font-bold transition-all',
          current?.preset === preset.value ? 'bg-primary text-white' : 'text-body hover:bg-gray-2',
        ]"
        @click="applyPreset(preset.value)"
      >
        {{ preset.label }}
      </button>

      <div class="mx-1 h-4 w-px bg-stroke" />

      <button
        type="button"
        :class="[
          'flex items-center gap-2 px-3 py-1.5 text-xs font-bold transition-all',
          current?.preset === 'custom' ? 'bg-primary text-white' : 'text-body hover:bg-gray-2',
        ]"
        @click="openCustomModal"
      >
        <CalendarDaysIcon class="h-4 w-4" />
        <span>{{ customButtonLabel }}</span>
      </button>

      <TimeRangeCustomModal
        :is-open="showCustomModal"
        :initial-start="customModalInitialStart"
        :initial-end="customModalInitialEnd"
        @close="closeCustomModal"
        @apply="applyCustomRange"
      />
    </div>
  </div>
</template>
